import * as zod from 'zod';

import { CartesianPose, CartesianPosition } from '@sb/geometry';
import {
  CameraIntegration,
  CameraIntrinsics,
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
  ExtrinsicsType,
  ChessboardParameters,
} from '@sb/integrations/types/cameraTypes';
import { three } from '@sb/utilities';

import { Space } from '../types';

import {
  Blob2D,
  Blob2DParams,
  LocateMethod,
  Shape2DParams,
  TemplateImage,
  TextPromptDetection2DParams,
} from './LocateTypes';
import {
  CalculateIntrinsicsDeprecatedResult,
  ChessboardCornersResult,
  CalibrationEntry,
  ClassifyClass,
  ClassifyResult,
  RegionOfInterest,
} from './VisionInterface';

/* classify */

const RunClassifyArgs = zod.object({
  method: zod.literal('classify'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  classes: ClassifyClass.array(),
});

const RunClassifyResult = zod.object({
  method: zod.literal('classify'),
  results: ClassifyResult.array(),
});

/* detect 2d blobs */

const RunDetect2DBlobsArgs = zod.object({
  method: zod.literal('detect2DBlobs'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  params: Blob2DParams,
});

export type RunDetect2DBlobsArgs = zod.infer<typeof RunDetect2DBlobsArgs>;

const RunDetect2DBlobsResult = zod.object({
  method: zod.literal('detect2DBlobs'),
  results: Blob2D.array(),
});

/* detect 2d shapes */

const RunDetect2DShapesArgs = zod.object({
  method: zod.literal('detect2DShapes'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  params: Shape2DParams,
  templateImage: TemplateImage,
});

export type RunDetect2DShapesArgs = zod.infer<typeof RunDetect2DShapesArgs>;

const RunDetect2DShapesResult = zod.object({
  method: zod.literal('detect2DShapes'),
  results: Blob2D.array(),
});

/* detect 2d text prompts */

const RunDetect2DTextPromptArgs = zod.object({
  method: zod.literal('detect2DTextPrompt'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  params: TextPromptDetection2DParams,
});

export type RunDetect2DTextPromptArgs = zod.infer<
  typeof RunDetect2DTextPromptArgs
>;

const RunDetect2DTextPromptResult = zod.object({
  method: zod.literal('detect2DTextPrompt'),
  results: Blob2D.array(),
});

/* detect custom checkpoint 2d */

const RunDetectCustomCheckpoint2DArgs = zod.object({
  method: zod.literal('detectCustomCheckpoint2D'),
  camera: CameraIntegration.optional(),
  // Optional equipment ID for camera-bot-cpp mode (when set, camera field is ignored)
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  locateMethod: LocateMethod,
});

export type RunDetectCustomCheckpoint2DArgs = zod.infer<
  typeof RunDetectCustomCheckpoint2DArgs
>;

const RunDetectCustomCheckpoint2DResult = zod.object({
  method: zod.literal('detectCustomCheckpoint2D'),
  results: Blob2D.array(),
});

/* get chessboard corners */

const RunGetChessboardCornersArgs = zod.object({
  method: zod.literal('getChessboardCorners'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  rows: zod.number(),
  cols: zod.number(),
});

const RunGetChessboardCornersResult = zod.object({
  method: zod.literal('getChessboardCorners'),
  results: ChessboardCornersResult,
});

/* get_camera_chessboard_transform */

const RunGetCameraChessboardTransformArgs = zod.object({
  method: zod.literal('getCameraChessboardTransform'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  chessboard: ChessboardParameters,
});

const RunGetCameraChessboardTransformResult = zod.object({
  method: zod.literal('getCameraChessboardTransform'),
  results: CartesianPose,
});

/* locate */

const RunLocateArgs = zod.object({
  method: zod.literal('locate'),
  camera: CameraIntegration.optional(),
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  locateMethod: LocateMethod,
  plane: zod.tuple(three(CartesianPosition)),
  resultsLimit: zod.number(),
  transform: CartesianPose.nullable(),
  accuracyCalibration: Space.AccuracyCalibrationEntry.array().nullable(),
  objectHeight: zod.number().default(0),
  intrinsics: CameraIntrinsics.optional(),
  extrinsics: ExtrinsicsKinematics.optional(),
});

const RunLocateResult = zod.object({
  method: zod.literal('locate'),
  results: zod
    .object({
      blob: Blob2D,
      pose: CartesianPose,
    })
    .array(),
});

export type RunLocateResult = zod.infer<typeof RunLocateResult>;

/* get intrinsics */

const GetIntrinsicsArgs = zod.object({
  method: zod.literal('getIntrinsics'),
  cameraEquipmentId: zod.string().optional(),
});

const GetIntrinsicsResult = zod.object({
  method: zod.literal('getIntrinsics'),
  results: zod.union([CameraIntrinsics, CameraIntrinsicsExtended]),
});

/* get built-in intrinsics */

const GetBuiltInIntrinsicsArgs = zod.object({
  method: zod.literal('getBuiltInIntrinsics'),
  cameraEquipmentId: zod.string().optional(),
});

const GetBuiltInIntrinsicsResult = zod.object({
  method: zod.literal('getBuiltInIntrinsics'),
  results: CameraIntrinsics,
});

/* calculate intrinsics (deprecated) */

const RunCalculateIntrinsicsDeprecatedArgs = zod.object({
  method: zod.literal('calculateIntrinsicsDeprecated'),
  cameraEquipmentId: zod.string().optional(),
  calibration: CalibrationEntry.array(),
  chessboard: ChessboardParameters,
});

const RunCalculateIntrinsicsDeprecatedResult = zod.object({
  method: zod.literal('calculateIntrinsicsDeprecated'),
  results: CalculateIntrinsicsDeprecatedResult,
});

/* calibrate camera intrinsics */
const RunCalibrateCameraIntrinsicsArgs = zod.object({
  method: zod.literal('calibrateCameraIntrinsics'),
  cameraEquipmentId: zod.string().optional(),
  calibration: CalibrationEntry.array(),
  chessboard: ChessboardParameters,
});

const RunCalibrateCameraIntrinsicsResult = zod.object({
  method: zod.literal('calibrateCameraIntrinsics'),
  results: CameraIntrinsicsExtended,
});

/* calibrate camera extrinsics */
const RunCalibrateCameraExtrinsicsArgs = zod.object({
  method: zod.literal('calibrateCameraExtrinsics'),
  cameraEquipmentId: zod.string().optional(),
  calibration: CalibrationEntry.array(),
  chessboard: ChessboardParameters,
  intrinsics: CameraIntrinsicsExtended,
  extrinsicsType: ExtrinsicsType,
});

const RunCalibrateCameraExtrinsicsResult = zod.object({
  method: zod.literal('calibrateCameraExtrinsics'),
  results: CartesianPose,
});

/* union */

export const RunVisionMethodArgs = zod.discriminatedUnion('method', [
  RunClassifyArgs,
  RunDetect2DBlobsArgs,
  RunDetect2DShapesArgs,
  RunDetect2DTextPromptArgs,
  RunDetectCustomCheckpoint2DArgs,
  RunGetChessboardCornersArgs,
  RunGetCameraChessboardTransformArgs,
  RunLocateArgs,
  GetIntrinsicsArgs,
  GetBuiltInIntrinsicsArgs,
  RunCalculateIntrinsicsDeprecatedArgs,
  RunCalibrateCameraIntrinsicsArgs,
  RunCalibrateCameraExtrinsicsArgs,
]);

export type RunVisionMethodArgs = zod.infer<typeof RunVisionMethodArgs>;

export const RunVisionMethodResult = zod.discriminatedUnion('method', [
  RunClassifyResult,
  RunDetect2DBlobsResult,
  RunDetect2DShapesResult,
  RunDetect2DTextPromptResult,
  RunDetectCustomCheckpoint2DResult,
  RunGetChessboardCornersResult,
  RunGetCameraChessboardTransformResult,
  RunLocateResult,
  GetIntrinsicsResult,
  GetBuiltInIntrinsicsResult,
  RunCalculateIntrinsicsDeprecatedResult,
  RunCalibrateCameraIntrinsicsResult,
  RunCalibrateCameraExtrinsicsResult,
]);

export type RunVisionMethodResult = zod.infer<typeof RunVisionMethodResult>;
